import type { Entry } from '@ozone/shared';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { EntryRow } from './EntryRow';
import { DraftEditor } from './DraftEditor';

interface CategorySubsectionProps {
  category: string;
  entries: Entry[];
  onToggleInclude?: (entryId: string, next: boolean) => void;
  onSaveDraft?: (entryId: string, md: string) => Promise<void> | void;
  onSavePeriod?: (entryId: string, mbrPeriod: string) => Promise<void> | void;
  onRegenerate?: (entryId: string) => Promise<void> | void;
}

/**
 * One category sub-section inside a SubmitterCard. Renders the category
 * name as an h3 with a small entry-count badge, then a separator-joined
 * stack of EntryRow's.
 */
export function CategorySubsection({
  category,
  entries,
  onToggleInclude = () => {},
  onSaveDraft = () => {},
  onSavePeriod = () => {},
  onRegenerate = () => {},
}: CategorySubsectionProps) {
  return (
    <section className="flex flex-col gap-1">
      <div className="flex items-center gap-2 px-4 pt-1">
        <h3 className="text-sm font-medium uppercase tracking-wide text-slate-500">
          {category}
        </h3>
        <Badge
          variant="secondary"
          className="bg-slate-100 text-slate-600 font-medium"
        >
          {entries.length}
        </Badge>
      </div>
      <div className="flex flex-col gap-4">
        {entries.map((e, i) => (
          <div key={e.entryId}>
            {i > 0 && <Separator className="my-0" />}
            <div className="flex flex-col gap-4 rounded-lg border border-slate-200 bg-white p-6">
              {/* Verbatim "Submitted" reference block — compact + muted, on top */}
              <div className="flex flex-col gap-1 rounded-md bg-slate-50/60 p-3">
                <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                  Submitted
                </span>
                <EntryRow entry={e} onToggleInclude={onToggleInclude} />
              </div>
              <DraftEditor
                entry={e}
                onSaveDraft={onSaveDraft}
                onSavePeriod={onSavePeriod}
                onRegenerate={onRegenerate}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
